import { createClient } from '@supabase/supabase-js';

// Environment variable checks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase environment variables:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey,
  });
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in Vercel.'
  );
}

/**
 * Build COMPLETE Supabase options object
 * CRITICAL: Must be fully explicit to prevent Vite tree-shaking internal defaults
 */
function buildSupabaseOptions() {
  // Ensure we're in browser context
  if (typeof window === 'undefined') {
    throw new Error('Supabase can only be initialized in browser context');
  }

  // Build options with ALL properties explicitly defined
  const options = {
    db: {
      schema: 'public',
    },
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
      storage: window.localStorage,
      storageKey: 'sb-auth-token',
      debug: false,
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
    global: {
      // CRITICAL: Explicitly bind fetch to window to prevent "this" context issues
      fetch: window.fetch.bind(window),
      // CRITICAL: Provide headers as a plain object (even if empty)
      headers: {
        'X-Client-Info': 'outsera-web@1.0.0',
      },
    },
  };

  return options;
}

let supabaseClient = null;
let initializationError = null;

/**
 * Get or create Supabase client
 * @returns {SupabaseClient} Initialized Supabase client
 * @throws {Error} If client cannot be initialized
 */
export function getSupabaseClient() {
  // If previous initialization failed, throw the same error
  if (initializationError) {
    throw initializationError;
  }

  if (!supabaseClient) {
    try {
      // Browser-only check
      if (typeof window === 'undefined') {
        throw new Error('Supabase client can only be initialized in the browser');
      }

      // Build complete options
      const options = buildSupabaseOptions();

      // Create client with complete options
      supabaseClient = createClient(supabaseUrl, supabaseAnonKey, options);
      
    } catch (error) {
      initializationError = new Error(
        `Supabase initialization failed: ${error?.message ?? 'Unknown error'}`
      );
      console.error('❌ Failed to create Supabase client:', {
        error: error,
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      });
      throw initializationError;
    }
  }

  return supabaseClient;
}

/**
 * Check if Supabase is available (doesn't throw)
 * @returns {boolean} True if Supabase client is available
 */
export function isSupabaseAvailable() {
  try {
    getSupabaseClient();
    return true;
  } catch (error) {
    console.warn('⚠️ Supabase not available:', error.message);
    return false;
  }
}

/**
 * Safe Supabase getter - returns null instead of throwing
 * Use this in contexts where Supabase failure shouldn't crash the app
 * @returns {SupabaseClient|null}
 */
export function getSafeSupabaseClient() {
  try {
    return getSupabaseClient();
  } catch (error) {
    console.warn('⚠️ Supabase client not available:', error.message);
    return null;
  }
}

/**
 * Lazy proxy for backwards compatibility
 * WARNING: This will throw if Supabase fails to initialize
 * Consider using getSafeSupabaseClient() instead for new code
 */
export const supabase = new Proxy({}, {
  get(_target, prop) {
    const client = getSupabaseClient();
    const value = client[prop];
    return typeof value === 'function' ? value.bind(client) : value;
  },
});

// Storage buckets
export const STORAGE_BUCKETS = {
  ORIGINALS: 'nft-originals',
  PREVIEWS: 'nft-previews'
};

// Database tables
export const TABLES = {
  CREATORS: 'creators',
  PIECES: 'pieces',
  CAPSULES: 'capsules',
  CAPSULE_PIECES: 'capsule_pieces',
  VERIFICATION_REQUESTS: 'verification_requests',
  USER_VERIFICATION_STATUS: 'user_verification_status'
};
